import { useState } from "react";
import { toast } from "sonner";
import { Megaphone, Send } from "lucide-react";
import { adminBroadcastNotification } from "@/lib/apiClient";

const ROLES = [
  { value: "all", label: "Everyone" },
  { value: "student", label: "Students" },
  { value: "mentor", label: "Mentors" },
  { value: "teacher", label: "Teachers (school)" },
  { value: "principal", label: "Principals" },
  { value: "support", label: "Support" },
  { value: "admin", label: "Admins" },
];

export default function AdminBroadcast() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [role, setRole] = useState("all");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!title.trim() || !body.trim()) return;
    setSending(true);
    try {
      const res = await adminBroadcastNotification({
        title: title.trim(),
        body: body.trim(),
        link: link.trim() || null,
        role,
      });
      toast.success(`Sent to ${res.sent} user${res.sent !== 1 ? "s" : ""}`);
      setTitle(""); setBody(""); setLink("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send broadcast");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-broadcast">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#1F2937] inline-flex items-center gap-2">
        <Megaphone className="w-6 h-6 text-[#064E3B]" /> Broadcast
      </h1>
      <p className="text-sm text-[#4B5563] mt-2 max-w-xl">
        Send a notification to appear in the bell icon for some or all users. Use sparingly — this reaches everyone in the selected group at once.
      </p>

      <div className="mt-6 max-w-xl rounded-2xl bg-white border border-[#E7E0D2] p-6 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-[0.16em] text-[#4B5563] mb-1.5 block">Send to</label>
          <select data-testid="broadcast-role" value={role} onChange={(e) => setRole(e.target.value)}
            className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.16em] text-[#4B5563] mb-1.5 block">Title</label>
          <input data-testid="broadcast-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
            placeholder="e.g. New feature: Peer Review is here"
            className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.16em] text-[#4B5563] mb-1.5 block">Message</label>
          <textarea data-testid="broadcast-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4} maxLength={1000}
            placeholder="What do you want to tell them?"
            className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm resize-none" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.16em] text-[#4B5563] mb-1.5 block">Link (optional)</label>
          <input data-testid="broadcast-link" value={link} onChange={(e) => setLink(e.target.value)} maxLength={200}
            placeholder="e.g. /modules"
            className="w-full px-3 py-2 rounded-xl bg-white border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
        </div>
        <button data-testid="broadcast-send" onClick={send} disabled={sending || !title.trim() || !body.trim()}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] hover:bg-[#047857] disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium text-sm transition">
          <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send broadcast"}
        </button>
      </div>
    </div>
  );
}
